<template>
  <v-input ref="field" :value="number" :rules="rules" :hide-details="hideDetails" class="company-phone-input">
    <VuePhoneNumberInput
      v-model="number"
      :default-country-code="countryCode"
      :error="showError"
      :clearable="clearable"
      :translations="translations"
      class="company-phone-input__field"
      @update="onUpdate" />
  </v-input>
</template>

<script>
import { parsePhone } from "@/util/phone";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";

// Wraps VuePhoneNumberInput so that v-model carries the phone in the format it is stored in
// (international when valid) instead of the national number the input edits. Validation runs
// through v-input, so the field registers with the surrounding v-form and its validate() covers it.
export default {
  name: "CompanyPhoneInput",
  components: { VuePhoneNumberInput },
  props: {
    value: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: "Phone Number",
    },
    required: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: "auto",
    },
  },
  data: () => ({
    number: null,
    countryCode: "SE",
    isValid: false,
    lastEmitted: undefined,
    hasFieldError: false,
  }),
  computed: {
    rules() {
      // Read here rather than inside the rules, so a change in either rebuilds the rules and
      // makes v-input validate again.
      const { required, isValid } = this;

      return [
        (number) => !required || !!number || "Phone number is required.",
        (number) => !number || isValid || "Phone number is not valid.",
      ];
    },
    showError() {
      return this.hasFieldError || (!!this.number && !this.isValid);
    },
    translations() {
      return { phoneNumberLabel: this.label };
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (value === this.lastEmitted) return;
        this.syncFromValue(value);
      },
    },
  },
  mounted() {
    this.$watch(
      () => this.$refs.field.validationState === "error",
      (hasError) => {
        this.hasFieldError = hasError;
      },
      { immediate: true }
    );
  },
  methods: {
    syncFromValue(value) {
      const parsed = parsePhone(value);

      // A stored number that cannot be parsed (e.g. a legacy one without a "+" prefix) stays
      // visible as it is but invalid, which blocks the save until it is fixed.
      this.number = parsed ? parsed.nationalNumber : value || null;
      this.countryCode = parsed ? parsed.countryCode : "SE";
      this.isValid = !!parsed;

      // Normalize here instead of relying on the input's `update`: it only fires on mount or on a
      // country change, so a reused input would leave e.g. a legacy "0701234567" unconverted.
      this.lastEmitted = parsed ? parsed.formatted : value;
      if (this.lastEmitted !== value) this.$emit("input", this.lastEmitted);

      // A new value from outside means a different record, so its errors should not carry over.
      this.$nextTick(() => this.$refs.field && this.$refs.field.resetValidation());
    },
    onUpdate(payload) {
      this.countryCode = payload.countryCode;
      // The component emits `update` before `input`, so payload.phoneNumber is the current value.
      // It is unset while the input is empty.
      this.isValid = payload.phoneNumber ? payload.isValid : false;

      const phone = this.isValid ? payload.formatInternational : payload.phoneNumber || null;
      if (phone === this.value) return;

      this.lastEmitted = phone;
      this.$emit("input", phone);
    },
    /** @public */
    validate(force = false) {
      return this.$refs.field.validate(force);
    },
  },
};
</script>

<style scoped>
.company-phone-input__field {
  width: 100%;
}
</style>
